import React, { useState } from "react";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import { refineSuggestionApi, type AiChatMessage, type AiSuggestion } from "../campanhasApi";

const SHORTCUTS = [
  "Troque a região por outra cidade.",
  "Use orçamento total de R$ 100.",
  "Mude as datas para começar na segunda.",
  "Use raio de 5 km em volta do endereço.",
  "Deixe os textos dos anúncios mais curtos e diretos.",
  "Peça currículo obrigatório.",
];

type Props = {
  suggestion: AiSuggestion;
  /** A sugestão nova, já ajustada; a tela inteira se atualiza com ela. */
  onChange: (s: AiSuggestion) => void;
  disabled?: boolean;
};

/**
 * Editar a sugestão conversando com a Eco-IA: a pessoa pede ("troque a região
 * por Osasco"), a Eco-IA devolve a sugestão inteira com a mudança, e a revisão
 * logo abaixo já mostra o novo texto. Nada é gravado até criar o rascunho.
 */
const SuggestionRefineChat: React.FC<Props> = ({ suggestion, onChange, disabled }) => {
  const [history, setHistory] = useState<AiChatMessage[]>([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const send = async (instruction: string) => {
    const ask = instruction.trim();
    if (!ask || busy) return;
    try {
      setBusy(true);
      setHistory((h) => [...h, { role: "user", content: ask }]);
      setText("");
      const out = await refineSuggestionApi(suggestion, ask, history);
      // A "próxima" carrega os anúncios da campanha anterior: eles continuam.
      onChange({ ...out.suggestion, sourceCampaignId: suggestion.sourceCampaignId, reuseAds: suggestion.reuseAds });
      setHistory((h) => [...h, { role: "assistant", content: out.reply || "Pronto, ajustei a sugestão." }]);
      toast.success("Sugestão atualizada.");
    } catch (e: any) {
      setHistory((h) => h.slice(0, -1));
      setText(ask);
      toast.error(e?.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="ecoCard cmpPanel aiRefine" aria-label="Ajustar a sugestão com a Eco-IA">
      <h2 className="cmpPanel__title">
        <EcoIcon name="helpCircle" size={18} /> Quer mudar algo? Peça à Eco-IA
      </h2>
      <p className="cmpHint">
        Escreva o que mudar: a região, o orçamento, as datas, os textos, as perguntas. A sugestão abaixo se atualiza com a mudança.
      </p>

      {history.length ? (
        <div className="aiRefine__log" aria-live="polite">
          {history.map((m, i) => (
            <div key={i} className={`aiMsg aiMsg--${m.role}`}>
              {m.content}
            </div>
          ))}
          {busy ? <div className="aiMsg aiMsg--assistant aiMsg--typing">Ajustando a sugestão... leva até um minuto.</div> : null}
        </div>
      ) : (
        <div className="aiDrawer__starters">
          {SHORTCUTS.map((s) => (
            <button key={s} type="button" className="aiChip" disabled={busy || disabled} onClick={() => setText(s)}>
              {s}
            </button>
          ))}
        </div>
      )}

      <form
        className="aiRefine__input"
        onSubmit={(e) => {
          e.preventDefault();
          send(text);
        }}
      >
        <textarea
          className="ecoInput cmpTextarea"
          rows={2}
          maxLength={1500}
          aria-label="O que mudar na sugestão"
          placeholder="Ex.: Troque a região por Osasco e use orçamento total de R$ 100."
          value={text}
          disabled={busy || disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send(text);
            }
          }}
        />
        <button type="submit" className="ecoBtn ecoBtn--primary" disabled={busy || disabled || !text.trim()}>
          {busy ? "Ajustando..." : "Pedir ajuste"}
        </button>
      </form>
    </section>
  );
};

export default SuggestionRefineChat;
